import { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Form from "./components/Form";

import { useNotifyContext, NotifyType } from "../../../contexts/NotifyContext";

import { Head } from "../../../components/core";

type Customer = {
  id: string;
  customerName: string;
};
type CustomerGroup = {
  id: string;
  name: string;
  description: string;
  customers: Customer[];
  status: boolean;
};

const FETCH_CUSTOMERGROUPS = gql`
  query FetchCustomerGroup($id: ID!) {
    fetchCustomerGroup(id: $id) {
      id
      name
      description
      customers {
        id
        customerName
      }
      status
    }
  }
`;

const CustomerGroupUpdate = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { addNotify } = useNotifyContext();
  let { customerGroupId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [customerGroup, setCustomerGroup] = useState<CustomerGroup>();

  const [fetchCustomerGroup] = useLazyQuery(FETCH_CUSTOMERGROUPS);

  useEffect(() => {
    fetchCustomerGroup({
      variables: {
        id: customerGroupId,
      },
    })
      .then(({ data, error }) => {
        if (data?.fetchCustomerGroup) {
          setCustomerGroup(data.fetchCustomerGroup);
        } else {
          return navigate("/error/401", {
            state: {
              message: error
                ? error.message
                : "Customer Group read permission(s) is required to access this page.",
            },
          });
        }
      })
      .catch((error) => {
        return navigate("/error/500", {
          state: {
            error,
            message: error.message,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCustomerGroup, customerGroupId]);

  const UPDATE_CUSTOMERGROUP = gql`
    mutation CustomerGroupUpdate(
      $id: ID!
      $name: String!
      $description: String
      $customerIds: [Int!]
      $status: Boolean!
    ) {
      customerGroupUpdate(
        input: {
          id: $id
          params: {
            name: $name
            description: $description
            customerIds: $customerIds
            status: $status
          }
        }
      ) {
        customerGroup {
          id
          name
          description
          customers {
            id
            customerName
          }
          status
        }
      }
    }
  `;
  const [updateCustomerGroup] = useMutation(UPDATE_CUSTOMERGROUP);

  const handleSubmit = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    updateCustomerGroup({
      variables: {
        id: customerGroup?.id,
        name: values.name,
        description: values.description,
        customerIds: values.customers ? values.customers : [],
        status: values.status,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.customerGroupUpdate) {
          addNotify({
            type: NotifyType.SUCCESS,
            title: "Customer Group updated successfully",
          });
          return navigate(`/sales/customer-groups`);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Customer Group update failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Customer Group update failed",
          message: error.message,
        });
      });
  };

  return (
    <>
      <Head
        title={CustomerGroupUpdateResource.name}
        heading="Customer Groups"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Customer Groups",
            href: "/sales/customer-groups",
          },
          {
            name: CustomerGroupUpdateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-3xl py-6 sm:py-8">
        <div className="rounded-xl bg-greyish px-4 py-5 sm:p-6">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_update_customer_group")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_update_customer_group")}
              </p>
            </div>
            {/* <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                <Link
                  to="/sales/customer-groups/add"
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
                >
                  Add customer group
                </Link>
              </div> */}
          </div>

          <Form
            initialValues={{
              name: customerGroup?.name ?? "",
              description: customerGroup?.description ?? "",
              customers: customerGroup?.customers
                ? customerGroup.customers.map((p) => parseInt(p.id))
                : [],
              status: customerGroup?.status ? true : false,
            }}
            onSubmit={handleSubmit}
            actionLabel={t("text_update")}
          />
        </div>
      </div>
    </>
  );
};

export default CustomerGroupUpdate;
export const CustomerGroupUpdateResource: ResourceProps = {
  name: "Edit Customer Group",
  description: "Update an existing customer group",
  access: ["update-customergroups"],
  path: "customer-groups/:customerGroupId",
};
